/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { dateUtils } from '@opengeoweb/shared';
import type { Dimension, WMLayer, WMJSDimension } from '@opengeoweb/webmap';
import type { UpdateLayerInfoPayload } from '../types/types';

export function getIsInsideAcceptanceTime(
  acceptanceTimeInMinutes: number | null | undefined,
  mapDimensions: Dimension[] | undefined,
  layerDimensions: Dimension[] | undefined,
): boolean {
  const mapCurrentTime = mapDimensions?.find(
    (dimension) => dimension.name === 'time',
  )?.currentValue;

  const layerCurrentTime = layerDimensions?.find(
    (dimension) => dimension.name === 'time',
  )?.currentValue;

  if (
    acceptanceTimeInMinutes === undefined ||
    acceptanceTimeInMinutes === null ||
    !mapCurrentTime ||
    !layerCurrentTime
  ) {
    return true;
  }

  const minutesBetween = dateUtils.differenceInMinutes(
    new Date(mapCurrentTime),
    new Date(layerCurrentTime),
  );
  if (Math.abs(minutesBetween) > acceptanceTimeInMinutes) {
    return false;
  }

  return true;
}

export const getLayerUpdateInfo = (
  wmLayer: WMLayer,
  mapId: string,
): UpdateLayerInfoPayload => {
  const origin = 'ReactMapViewParseLayer';

  const layerDimensions = wmLayer.dimensions.map((dim: WMJSDimension) => {
    const { name, units, synced } = dim;
    return {
      name,
      units,
      currentValue: dim.getValue(),
      minValue: dim.getFirstValue(),
      maxValue: dim.getLastValue(),
      timeInterval: dim.getDimInterval(),
      values: dim.getValues(),
      synced,
    };
  });
  /* Update list of layers for service */
  const updateObject: UpdateLayerInfoPayload = {
    origin,
    layerStyle: {
      origin,
      layerId: wmLayer.ReactWMJSLayerId!,
      style: wmLayer.currentStyle!,
    },
    mapDimensions: {
      mapId,
      origin,
      dimensions: [],
    },
    layerDimensions: {
      origin,
      layerId: wmLayer.id,
      dimensions: layerDimensions,
    },
    error: wmLayer.hasError ? { layerId: wmLayer.id } : undefined,
  };
  return updateObject;
};
